import { SimCardDownload } from '@mui/icons-material';
import { Grid, Card, Box, Button, CircularProgress } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { downloadDepositPdf } from 'src/shared/functions/downloadDepositPdf';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getPdfByDepositId } from 'src/shared/services/depositService';

type GiftDetailAttachmentProps = {
  depositId: number;
  hasAttachemnet: boolean;
};

export const GiftDetailAttachment = (props: GiftDetailAttachmentProps) => {
  const { depositId, hasAttachemnet } = props;
  const { t } = useTranslation(['gift']);
  const divRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPdf, setLoadingPdf] = useState<boolean>(false);

  const isMounted = useIsMounted();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getPdfByDepositId(depositId);
        if (isMounted()) {
          ConsoleHelper.log(result.data);
          const pdfBlob = new Blob([new Uint8Array(result.data, 0)], { type: 'application/pdf' });
          const pdfViewer = document.createElement('iframe');
          pdfViewer.src = URL.createObjectURL(pdfBlob);
          pdfViewer.onload = () => URL.revokeObjectURL(pdfViewer.src);
          pdfViewer.style.height = 'auto';
          pdfViewer.style.width = '100%';
          pdfViewer.style.border = '0';
          if (divRef.current) {
            divRef.current.replaceChildren(pdfViewer);
          }
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.log(e);
        }
      } finally {
        if (isMounted()) {
          setLoading(false);
        }
      }
    };
    if (hasAttachemnet) {
      fetchData();
    }
  }, [depositId, hasAttachemnet, isMounted]);

  const pdfLoadingCallback = useCallback((isLoading: boolean) => {
    setLoadingPdf(isLoading);
  }, []);

  const downloadPdf = async () => {
    downloadDepositPdf(depositId, pdfLoadingCallback);
  };

  if (!hasAttachemnet) return <></>;

  return (
    <Grid container spacing={2} sx={{ height: '100%' }}>
      {loading && (
        <Grid item xs={12}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={80} />
          </Box>
        </Grid>
      )}
      <Grid item xs={12} sx={{ minHeight: '20rem', display: 'flex', visibility: loading ? 'hidden' : 'visible', position: 'relative' }}>
        {loadingPdf && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, right: 0, textAlign: 'center', backgroundColor: 'rgba(255,255,255,0.7)' }}>
            <CircularProgress size={80} sx={{ mt: '10rem' }} />
          </Box>
        )}
        <Card variant='outlined' sx={{ minHeight: '90%', display: 'flex', width: '100%' }} ref={divRef} />
      </Grid>
      {!loading && (
        <Grid item xs={12}>
          <Button fullWidth variant='outlined' endIcon={<SimCardDownload />} onClick={() => downloadPdf()}>
            {t('gift:donorsDetailPage.downloadPDF')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
